/**
 * Section B7.2 Page - B.7.2.專案計算
 * UI 模式：DataTable + Modal（頁籤開關）
 * 主頁面顯示方程式清單，新增/編輯透過 Modal 內 6 個頁籤自由切換操作。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import LatexHelper from '../components/LatexHelper';
import sectionB72Service, {
  SectionB72Detail, EquationEntry, MethodChoiceItem,
} from '../services/sectionB72Service';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import '../styles/DataTable.css';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import { parseLatexSymbols, normalizeSubscripts } from '../utils/latexEvaluator';
import formulaConfigService, { FormulaConfigItem } from '../services/formulaConfigService';

// ── 數值格式化 ────────────────────────────────────────────────────
function fmtNum(v: number | null | undefined, d = 2): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });
}

// ── LaTeX 自動計算 ────────────────────────────────────────────────
function evaluateLatex(latex: string, params: MethodChoiceItem[]): number | null {
  try {
    let expr = latex.includes('=')
      ? latex.split(/(?<![<>!])=(?!=)/).slice(1).join('=')
      : latex;
    // 正規化頂層單字元下標（OX_y → OX_{y}）
    { let r = '', d = 0, i = 0; while (i < expr.length) { const c = expr[i]; if (c === '\\') { r += c + (expr[i+1]??''); i+=2; } else if (c === '{') { d++; r+=c; i++; } else if (c === '}') { d--; r+=c; i++; } else if (d===0 && c==='_' && i+1<expr.length && /[A-Za-z0-9]/.test(expr[i+1])) { r+='_{'+expr[i+1]+'}'; i+=2; } else { r+=c; i++; } } expr=r; }
    // 依符號長度由長到短替換，避免短符號污染長符號
    const filled = [...params]
      .filter(p => p.annual_value !== null && p.symbol.trim())
      .sort((a, b) => b.symbol.length - a.symbol.length);
    for (const p of filled) {
      const sym = normalizeSubscripts(p.symbol);
      const escaped = sym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expr = expr.replace(new RegExp(escaped, 'g'), `(${p.annual_value})`);
    }
    expr = expr
      .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1)/($2)')
      .replace(/\\sqrt\{([^}]*)\}/g, 'Math.sqrt($1)')
      .replace(/\\(?:sum|prod|int)(?:_\{[^}]*\})?(?:\^\{[^}]*\})?/g, '')
      .replace(/\\times/g, '*').replace(/\\div/g, '/').replace(/\\cdot/g, '*')
      .replace(/\\left\s*\(/g, '(').replace(/\\right\s*\)/g, ')')
      .replace(/\\left\s*\[/g, '(').replace(/\\right\s*\]/g, ')')
      .replace(/\[/g, '(').replace(/\]/g, ')')
      .replace(/\\left\s*\\{/g, '(').replace(/\\right\s*\\}/g, ')')
      .replace(/\^\{([^}]*)\}/g, '**($1)').replace(/\^([0-9]+)/g, '**$1')
      .replace(/\\[a-zA-Z]+/g, '')
      .replace(/\{/g, '(').replace(/\}/g, ')')
      .replace(/_/g, '');
    if (/[^0-9+\-*/().%\s,MathsqrtpowPIE]/.test(expr.replace(/Math\.[a-z]+/g, ''))) return null;
    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${expr})`)();
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result * 100000) / 100000;
  } catch { return null; }
}

// ── 常用單位 ──────────────────────────────────────────────────────
const COMMON_UNITS = [
  { label: 'tCO₂e', latex: '\\text{tCO}_2\\text{e}' },
  { label: 'ktCO₂e', latex: '\\text{ktCO}_2\\text{e}' },
  { label: 'MtCO₂e', latex: '\\text{MtCO}_2\\text{e}' },
  { label: 'MWh', latex: '\\text{MWh}' }, { label: 'GWh', latex: '\\text{GWh}' },
  { label: 'kWh', latex: '\\text{kWh}' }, { label: 'GJ', latex: '\\text{GJ}' },
  { label: 'MJ', latex: '\\text{MJ}' }, { label: 'kg', latex: '\\text{kg}' },
  { label: 't', latex: '\\text{t}' }, { label: 'm³', latex: '\\text{m}^3' },
  { label: 'Nm³', latex: '\\text{Nm}^3' }, { label: 'L', latex: '\\text{L}' },
  { label: '%', latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ', latex: '\\text{tCO}_2\\text{e/GJ}' },
];

// ── UnitInput ─────────────────────────────────────────────────────
interface UnitInputProps { value: string; onChange: (v: string) => void; disabled?: boolean; }
const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const preview = () => {
    if (!value.trim()) return null;
    try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          placeholder="LaTeX，如 \text{tCO}_2\text{e}"
          style={{ flex: 1, padding: '6px 8px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 12 }} />
        {value.trim() && (
          <div style={{ minWidth: 50, padding: '2px 6px', background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {preview()}
          </div>
        )}
        {!disabled && (
          <button type="button" onClick={() => setShowPicker(p => !p)}
            style={{ padding: '4px 7px', fontSize: 13, borderRadius: 5, border: '1px solid #d0d0d0', background: showPicker ? '#4A90E2' : '#fafafa', color: showPicker ? '#fff' : '#555', cursor: 'pointer' }}>≡</button>
        )}
      </div>
      {showPicker && (
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 300, marginTop: 4, background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 300 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位（點擊插入）</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {COMMON_UNITS.map((u, i) => (
              <button key={i} type="button" onClick={() => { onChange(u.latex); setShowPicker(false); }}
                style={{ padding: '2px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #e0e0e0', background: '#fafafa', cursor: 'pointer' }}>
                {u.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Toggle Switch ─────────────────────────────────────────────────
interface ToggleProps { value: boolean; onChange: (v: boolean) => void; disabled?: boolean; }
const Toggle: React.FC<ToggleProps> = ({ value, onChange, disabled = false }) => (
  <div onClick={() => !disabled && onChange(!value)}
    style={{ width: 48, height: 26, borderRadius: 13, background: value ? '#4A90E2' : '#ccc', position: 'relative', cursor: disabled ? 'default' : 'pointer', transition: 'background .2s', flexShrink: 0 }}>
    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: value ? 25 : 3, transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
  </div>
);

// ── SaveFeedback ──────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ════════════════════════════════════════════════════════════════
// Section Props 共用介面
// ════════════════════════════════════════════════════════════════
interface SectionProps {
  entry: EquationEntry;
  projectId: number;
  canEdit: boolean;
  isZh: boolean;
  onRefresh: (d: SectionB72Detail) => void;
}

// ════════════════════════════════════════════════════════════════
// Section 1：計算公式
// ════════════════════════════════════════════════════════════════
const Section1Formula: React.FC<SectionProps> = ({ entry, projectId, canEdit, isZh, onRefresh }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [methodology, setMethodology] = useState(entry.methodology);
  const [equation, setEquation] = useState(entry.equation);
  const [note, setNote] = useState(entry.equation_note);
  const [latexRef, setLatexRef] = useState<HTMLTextAreaElement | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMethodology(entry.methodology); setEquation(entry.equation); setNote(entry.equation_note);
  }, [entry]);

  const insertLatex = (snippet: string) => {
    if (!latexRef) { setEquation(v => v + snippet); return; }
    const s = latexRef.selectionStart ?? latexRef.value.length;
    const e = latexRef.selectionEnd ?? latexRef.value.length;
    const next = latexRef.value.slice(0, s) + snippet + latexRef.value.slice(e);
    setEquation(next);
    setTimeout(() => { latexRef.focus(); latexRef.setSelectionRange(s + snippet.length, s + snippet.length); }, 10);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB72Service.updateEquation(projectId, entry.id, { methodology, equation, equation_note: note });
      onRefresh(res); show(true, t('sectionB72.saveSuccess'));
    } catch { show(false, t('sectionB72.saveFailed')); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('sectionB72.methodologyLabel')}</label>
        <input value={methodology} onChange={e => setMethodology(e.target.value)} disabled={!canEdit}
          placeholder={t('sectionB72.methodologyPlaceholder')}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>{t('sectionB72.equationLabel')}</label>
          <LatexHelper isZh={isZh} onInsert={insertLatex} />
        </div>
        <textarea ref={setLatexRef} value={equation} onChange={e => setEquation(e.target.value)} disabled={!canEdit}
          rows={3} placeholder="PE_{y} = \sum_{i} \left( AD_{i,y} \times EF_{i,y} \right)"
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
        {equation.trim() && (
          <div style={{ marginTop: 6, padding: '8px 12px', background: '#f5f8ff', border: '1px solid #d0e4ff', borderRadius: 6, overflowX: 'auto' }}>
            <BlockMath math={equation} />
          </div>
        )}
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
          {t('sectionB72.noteLabel')} <span style={{ color: '#aaa', fontWeight: 400 }}>({t('sectionB72.optional')}, ex ante / ex post)</span>
        </label>
        <RichTextEditor value={note} onChange={setNote} disabled={!canEdit} minHeight="72px" />
      </div>
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB72.saving') : t('sectionB72.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Section 2：方法論說明（參數）
// ════════════════════════════════════════════════════════════════
interface Section2Props extends SectionProps { paraTypeCodes: SystemCode[]; }

const Section2MethodChoices: React.FC<Section2Props> = ({ entry, projectId, canEdit, isZh, onRefresh, paraTypeCodes }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [choices, setChoices] = useState<MethodChoiceItem[]>(entry.method_choices || []);
  const [saving, setSaving] = useState(false);
  const [formulaConfigs, setFormulaConfigs] = useState<FormulaConfigItem[]>([]);
  const [formulaId, setFormulaId] = useState<number | ''>('');

  useEffect(() => { formulaConfigService.listActive().then(setFormulaConfigs).catch(() => {}); }, []);

  useEffect(() => {
    const parsed = parseLatexSymbols(entry.equation);
    if (!entry.method_choices?.length && parsed.length) {
      setChoices(parsed.map(sym => ({ symbol: sym, para_type: null, scenarios: '', unit: '' })));
    } else {
      setChoices(entry.method_choices || []);
    }
  }, [entry]);

  const applyFormulaConfig = (id: number | '') => {
    setFormulaId(id);
    if (!id) return;
    const fc = formulaConfigs.find(f => f.id === id);
    if (!fc) return;
    const parsed = parseLatexSymbols(entry.equation);
    const newChoices: MethodChoiceItem[] = parsed.map(sym => {
      const cp = fc.parameters.find(p => p.symbol === sym);
      return { symbol: sym, para_type: null, scenarios: cp ? (isZh ? [cp.name_zh, cp.description_zh] : [cp.name_en || cp.name_zh, cp.description_en || cp.description_zh]).filter(Boolean).join(' ') : '', unit: cp?.unit ?? '' };
    });
    setChoices(newChoices);
  };

  const update = (idx: number, field: keyof MethodChoiceItem, value: any) =>
    setChoices(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  const addRow = () => setChoices(prev => [...prev, { symbol: '', para_type: null, scenarios: '', unit: '' }]);
  const removeRow = (idx: number) => setChoices(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB72Service.updateEquation(projectId, entry.id, { method_choices: choices });
      onRefresh(res); show(true, t('sectionB72.saveSuccess'));
    } catch { show(false, t('sectionB72.saveFailed')); } finally { setSaving(false); }
  };

  const getTypeLabel = (code: number | null | undefined) => {
    if (!code) return t('sectionB72.allTypes');
    const found = paraTypeCodes.find(c => c.id === code);
    return found ? (isZh ? found.code_cname : found.code_ename) : String(code);
  };

  return (
    <div>
      {/* 從公式庫帶入參數說明 */}
      {canEdit && formulaConfigs.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{isZh ? '從公式庫帶入：' : 'From library:'}</label>
          <select value={formulaId} onChange={e => applyFormulaConfig(e.target.value ? Number(e.target.value) : '')}
            style={{ flex: 1, padding: '6px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}>
            <option value="">-- {isZh ? '選擇套用（將覆蓋現有說明）' : 'Select to apply (overwrites descriptions)'} --</option>
            {formulaConfigs.map(f => <option key={f.id} value={f.id}>{f.formula_code}：{isZh ? f.name_zh : f.name_en}</option>)}
          </select>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
        <thead className="table-header-dark-green">
          <tr style={{ background: '#f5f5f5' }}>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB72.colSymbol')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 130 }}>{t('sectionB72.colParaType')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left' }}>{t('sectionB72.colScenarios')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 160 }}>{t('sectionB72.colUnit')}</th>
            {canEdit && <th style={{ width: 30 }}></th>}
          </tr>
        </thead>
        <tbody>
          {choices.map((c, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '5px 8px' }}>
                <input value={c.symbol} onChange={e => update(i, 'symbol', e.target.value)} disabled={!canEdit}
                  style={{ width: '100%', padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }} />
              </td>
              <td style={{ padding: '5px 8px' }}>
                {canEdit ? (
                  <select value={c.para_type ?? ''} onChange={e => update(i, 'para_type', e.target.value ? Number(e.target.value) : null)}
                    style={{ width: '100%', padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontSize: 12 }}>
                    <option value="">{t('sectionB72.allTypes')}</option>
                    {paraTypeCodes.map(code => (
                      <option key={code.id} value={code.id}>{isZh ? code.code_cname : code.code_ename}</option>
                    ))}
                  </select>
                ) : <span style={{ fontSize: 11, color: '#666' }}>{getTypeLabel(c.para_type)}</span>}
              </td>
              <td style={{ padding: '5px 8px', minWidth: 200 }}>
                <RichTextEditor
                  value={c.scenarios}
                  onChange={html => update(i, 'scenarios', html)}
                  disabled={!canEdit}
                  minHeight="60px"
                  toolbar="inline"
                />
              </td>
              <td style={{ padding: '5px 8px' }}>
                <UnitInput value={c.unit} onChange={v => update(i, 'unit', v)} disabled={!canEdit} />
              </td>
              {canEdit && (
                <td style={{ padding: '5px 4px', textAlign: 'center' }}>
                  <button onClick={() => removeRow(i)} style={{ padding: '2px 6px', fontSize: 11, border: '1px solid #e53935', borderRadius: 3, color: '#e53935', background: '#fff', cursor: 'pointer' }}>✕</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={addRow} style={{ padding: '5px 12px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', color: '#4A90E2', background: '#fff', cursor: 'pointer' }}>
            + {t('sectionB72.addParameter')}
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '5px 16px', fontSize: 12, borderRadius: 5, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB72.saving') : t('sectionB72.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Section 3：設備效能說明（選填）
// ════════════════════════════════════════════════════════════════
const Section3Performance: React.FC<SectionProps> = ({ entry, projectId, canEdit, onRefresh }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [isPerformance, setIsPerformance] = useState(entry.is_performance);
  const [desc, setDesc] = useState(entry.performance_equipment || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setIsPerformance(entry.is_performance); setDesc(entry.performance_equipment || ''); }, [entry]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB72Service.updateEquation(projectId, entry.id, { is_performance: isPerformance, performance_equipment: isPerformance ? desc : null });
      onRefresh(res); show(true, t('sectionB72.saveSuccess'));
    } catch { show(false, t('sectionB72.saveFailed')); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <label style={{ fontWeight: 600, fontSize: 13 }}>{t('sectionB72.isPerformance')}</label>
        <Toggle value={isPerformance} onChange={setIsPerformance} disabled={!canEdit} />
        <span style={{ fontSize: 13, color: isPerformance ? '#4A90E2' : '#999' }}>{isPerformance ? t('sectionB72.yes') : t('sectionB72.no')}</span>
      </div>
      {isPerformance && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>{t('sectionB72.performanceLabel')}</label>
          <RichTextEditor value={desc} onChange={setDesc} disabled={!canEdit} />
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB72.saving') : t('sectionB72.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Section 4：量測檢定標準與程序
// ════════════════════════════════════════════════════════════════
const Section4Procedures: React.FC<SectionProps> = ({ entry, projectId, canEdit, onRefresh }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [isProcedures, setIsProcedures] = useState(entry.is_procedures);
  const [desc, setDesc] = useState(entry.standards_procedures || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setIsProcedures(entry.is_procedures); setDesc(entry.standards_procedures || ''); }, [entry]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB72Service.updateEquation(projectId, entry.id, { is_procedures: isProcedures, standards_procedures: isProcedures ? desc : null });
      onRefresh(res); show(true, t('sectionB72.saveSuccess'));
    } catch { show(false, t('sectionB72.saveFailed')); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <label style={{ fontWeight: 600, fontSize: 13 }}>{t('sectionB72.isProcedures')}</label>
        <Toggle value={isProcedures} onChange={setIsProcedures} disabled={!canEdit} />
        <span style={{ fontSize: 13, color: isProcedures ? '#4A90E2' : '#999' }}>{isProcedures ? t('sectionB72.yes') : t('sectionB72.no')}</span>
      </div>
      {isProcedures && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>{t('sectionB72.proceduresLabel')}</label>
          <RichTextEditor value={desc} onChange={setDesc} disabled={!canEdit} />
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB72.saving') : t('sectionB72.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Section 5：保守評估說明
// ════════════════════════════════════════════════════════════════
const Section5Conservative: React.FC<SectionProps> = ({ entry, projectId, canEdit, onRefresh }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [choices, setChoices] = useState<MethodChoiceItem[]>([]);
  const [calcResult, setCalcResult] = useState(entry.calculation_result);
  const [conservativeDesc, setConservativeDesc] = useState(entry.conservative_description || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChoices((entry.method_choices || []).map(c => ({ ...c })));
    setCalcResult({ ...entry.calculation_result });
    setConservativeDesc(entry.conservative_description || '');
  }, [entry]);

  const updateValue = (idx: number, val: string) =>
    setChoices(prev => prev.map((c, i) => i === idx ? { ...c, annual_value: val === '' ? null : parseFloat(val) } : c));

  const handleCalc = () => {
    const result = evaluateLatex(entry.equation, choices);
    if (result !== null) {
      setCalcResult(prev => ({ ...prev, value: result }));
    } else {
      const manual = window.prompt(t('sectionB72.calcAutoError'));
      if (manual !== null) setCalcResult(prev => ({ ...prev, value: parseFloat(manual) || null }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB72Service.updateEquation(projectId, entry.id, {
        method_choices: choices, calculation_result: calcResult, conservative_description: conservativeDesc,
      });
      onRefresh(res); show(true, t('sectionB72.saveSuccess'));
    } catch { show(false, t('sectionB72.saveFailed')); } finally { setSaving(false); }
  };

  if (!choices.length) return <div style={{ color: '#999', fontSize: 13 }}>{t('sectionB72.noParamsYet')}</div>;

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 14 }}>
        <thead className="table-header-dark-green">
          <tr style={{ background: '#f5f5f5' }}>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB72.colSymbol')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left' }}>{t('sectionB72.colScenarios')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'left', width: 100 }}>{t('sectionB72.colUnit')}</th>
            <th style={{ padding: '5px 8px', textAlign: 'right', width: 130 }}>{t('sectionB72.colAnnualValue')}</th>
          </tr>
        </thead>
        <tbody>
          {choices.map((c, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '5px 8px' }}>
                <code style={{ fontSize: 11, background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>{c.symbol}</code>
              </td>
              <td style={{ padding: '5px 8px', fontSize: 11, color: '#666' }}>
                {c.scenarios
                  ? <span dangerouslySetInnerHTML={{ __html: c.scenarios }} />
                  : '—'}
              </td>
              <td style={{ padding: '5px 8px', fontSize: 11 }}>{c.unit ? <InlineMath math={c.unit} /> : '—'}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                <input type="number" value={c.annual_value ?? ''} onChange={e => updateValue(i, e.target.value)}
                  disabled={!canEdit}
                  style={{ width: 110, padding: '3px 6px', border: '1px solid #d0d0d0', borderRadius: 4, fontSize: 12, textAlign: 'right' }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {canEdit && (
          <button onClick={handleCalc}
            style={{ padding: '6px 14px', fontSize: 12, borderRadius: 5, border: '1px solid #4A90E2', background: '#4A90E2', color: '#fff', cursor: 'pointer' }}>
            ▶ {t('sectionB72.calculate')}
          </button>
        )}
        <span style={{ fontSize: 13, fontWeight: 500 }}>{t('sectionB72.calcResult')}</span>
        <span style={{ minWidth: 120, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 5, fontSize: 13, textAlign: 'right', background: '#f5f5f5', display: 'inline-block' }}>
          {calcResult.value != null ? fmtNum(calcResult.value) : '—'}
        </span>
        <span style={{ fontSize: 12, color: '#888' }}>{t('sectionB72.calcUnit')}</span>
        <div style={{ width: 200 }}>
          <UnitInput value={calcResult.unit} onChange={v => setCalcResult(prev => ({ ...prev, unit: v }))} disabled={!canEdit} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>{t('sectionB72.conservativeLabel')}</label>
        <RichTextEditor value={conservativeDesc} onChange={setConservativeDesc} disabled={!canEdit} />
      </div>
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB72.saving') : t('sectionB72.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Section 6：抽樣計畫說明（選填）
// ════════════════════════════════════════════════════════════════
const Section6Sampling: React.FC<SectionProps> = ({ entry, projectId, canEdit, onRefresh }) => {
  const { t } = useTranslation();
  const { msg, show } = useSaveFeedback();
  const [isSampling, setIsSampling] = useState(entry.is_sampling);
  const [plan, setPlan] = useState(entry.sampling_plan || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setIsSampling(entry.is_sampling); setPlan(entry.sampling_plan || ''); }, [entry]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await sectionB72Service.updateEquation(projectId, entry.id, { is_sampling: isSampling, sampling_plan: isSampling ? plan : null });
      onRefresh(res); show(true, t('sectionB72.saveSuccess'));
    } catch { show(false, t('sectionB72.saveFailed')); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <label style={{ fontWeight: 600, fontSize: 13 }}>{t('sectionB72.isSampling')}</label>
        <Toggle value={isSampling} onChange={setIsSampling} disabled={!canEdit} />
        <span style={{ fontSize: 13, color: isSampling ? '#4A90E2' : '#999' }}>{isSampling ? t('sectionB72.yes') : t('sectionB72.no')}</span>
      </div>
      {isSampling && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>{t('sectionB72.samplingLabel')}</label>
          <RichTextEditor value={plan} onChange={setPlan} disabled={!canEdit} />
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? t('sectionB72.saving') : t('sectionB72.save')}
          </button>
          {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// EquationModal：新增/編輯方程式 Modal（6 頁籤自由切換）
// ════════════════════════════════════════════════════════════════
interface EquationModalProps {
  projectId: number;
  initialEntry: EquationEntry | null; // null = 新增模式
  isZh: boolean;
  canEdit: boolean;
  paraTypeCodes: SystemCode[];
  onClose: () => void;
  onDataChange: (d: SectionB72Detail) => void;
}

const EquationModal: React.FC<EquationModalProps> = ({
  projectId, initialEntry, isZh, canEdit, paraTypeCodes, onClose, onDataChange,
}) => {
  const { t } = useTranslation();
  const isNew = initialEntry === null;

  // 新增模式：暫存輸入直到第一次儲存
  const [methodology, setMethodology] = useState('');
  const [equation, setEquation] = useState('');
  const [note, setNote] = useState('');
  const [latexRef, setLatexRef] = useState<HTMLTextAreaElement | null>(null);
  const [creating, setCreating] = useState(false);

  // 已存在的 entry（新增成功後或編輯時）
  const [entry, setEntry] = useState<EquationEntry | null>(initialEntry);
  const [activeTab, setActiveTab] = useState(0);

  const TABS = [
    t('sectionB72.tab1'), t('sectionB72.tab2'), t('sectionB72.tab3'),
    t('sectionB72.tab4'), t('sectionB72.tab5'), t('sectionB72.tab6'),
  ];

  const handleRefresh = (d: SectionB72Detail) => {
    onDataChange(d);
    if (entry) {
      const updated = (d.equations ?? []).find(e => e.id === entry.id);
      if (updated) setEntry(updated);
    }
  };

  const insertLatex = (snippet: string) => {
    if (!latexRef) { setEquation(v => v + snippet); return; }
    const s = latexRef.selectionStart ?? latexRef.value.length;
    const e = latexRef.selectionEnd ?? latexRef.value.length;
    setEquation(latexRef.value.slice(0, s) + snippet + latexRef.value.slice(e));
    setTimeout(() => { latexRef.focus(); latexRef.setSelectionRange(s + snippet.length, s + snippet.length); }, 10);
  };

  const handleCreate = async () => {
    if (!methodology.trim() && !equation.trim()) return;
    setCreating(true);
    try {
      const res = await sectionB72Service.addEquation(projectId, { methodology, equation, equation_note: note });
      onDataChange(res);
      const newEntry = (res.equations ?? []).at(-1);
      if (newEntry) { setEntry(newEntry); setActiveTab(1); } // 建立成功，跳到頁籤②
    } catch (err) { console.error(err); } finally { setCreating(false); }
  };

  return (
    // 全螢幕遮罩
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      {/* Modal 卡片 */}
      <div style={{
        background: '#fff', borderRadius: 10, width: 1200, height: '88vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
      }}>
        {/* 標題列 */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            {isNew && !entry ? t('sectionB72.newEquation') : `${t('sectionB72.editEquation')} — ${entry?.methodology || entry?.equation?.slice(0, 30) || ''}`}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
        </div>

        {/* 新增模式：第一步（尚未建立方程式）*/}
        {isNew && !entry ? (
          <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('sectionB72.methodologyLabel')}</label>
              <input value={methodology} onChange={e => setMethodology(e.target.value)}
                placeholder={t('sectionB72.methodologyPlaceholder')}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>{t('sectionB72.equationLabel')}</label>
                <LatexHelper isZh={isZh} onInsert={insertLatex} />
              </div>
              <textarea ref={setLatexRef} value={equation} onChange={e => setEquation(e.target.value)}
                rows={3} placeholder="PE_{y} = \sum_{i} \left( AD_{i,y} \times EF_{i,y} \right)"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
              {equation.trim() && (
                <div style={{ marginTop: 6, padding: '8px 12px', background: '#f5f8ff', border: '1px solid #d0e4ff', borderRadius: 6, overflowX: 'auto' }}>
                  <BlockMath math={equation} />
                </div>
              )}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                {t('sectionB72.noteLabel')} <span style={{ color: '#aaa', fontWeight: 400 }}>({t('sectionB72.optional')})</span>
              </label>
              <RichTextEditor value={note} onChange={setNote} minHeight="60px" />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleCreate} disabled={creating || (!methodology.trim() && !equation.trim())}
                style={{ padding: '8px 22px', fontSize: 13, borderRadius: 6, border: 'none', background: '#4A90E2', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                {creating ? t('sectionB72.saving') : t('sectionB72.save')}
              </button>
              <button onClick={onClose}
                style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
                {t('sectionB72.cancel')}
              </button>
            </div>
          </div>
        ) : entry ? (
          // 編輯模式：6 頁籤自由切換
          <>
            {/* 頁籤列 */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', flexShrink: 0, overflowX: 'auto' }}>
              {TABS.map((label, i) => (
                <button key={i} onClick={() => setActiveTab(i)}
                  style={{
                    padding: '10px 16px', fontSize: 12, border: 'none', borderBottom: activeTab === i ? '2px solid #4A90E2' : '2px solid transparent',
                    background: 'none', color: activeTab === i ? '#4A90E2' : '#555',
                    cursor: 'pointer', fontWeight: activeTab === i ? 600 : 400, whiteSpace: 'nowrap',
                  }}>
                  {`${['①','②','③','④','⑤','⑥'][i]} ${label}`}
                </button>
              ))}
            </div>

            {/* 頁籤內容 */}
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              {activeTab === 0 && <Section1Formula entry={entry} projectId={projectId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} />}
              {activeTab === 1 && <Section2MethodChoices entry={entry} projectId={projectId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} paraTypeCodes={paraTypeCodes} />}
              {activeTab === 2 && <Section3Performance entry={entry} projectId={projectId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} />}
              {activeTab === 3 && <Section4Procedures entry={entry} projectId={projectId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} />}
              {activeTab === 4 && <Section5Conservative entry={entry} projectId={projectId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} />}
              {activeTab === 5 && <Section6Sampling entry={entry} projectId={projectId} canEdit={canEdit} isZh={isZh} onRefresh={handleRefresh} />}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// 主元件 SectionB72Page
// ════════════════════════════════════════════════════════════════
const SectionB72Page: React.FC = () => {
  const { i18n, t } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const toast = useToast();

  const [data, setData] = useState<SectionB72Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [paraTypeCodes, setParaTypeCodes] = useState<SystemCode[]>([]);
  // null = modal 關閉, undefined = 新增模式, EquationEntry = 編輯模式
  const [modalEntry, setModalEntry] = useState<EquationEntry | null | undefined>(undefined);
  const modalOpen = modalEntry !== undefined;

  const canEdit = hasPermission('section_b7_2', 'update');

  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionB72Page');
      const result = await sectionB72Service.getSectionB72ByProject(projectId);
      setData(result);
    } catch (err) { console.error('載入失敗', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setData(null); setModalEntry(undefined); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  useEffect(() => {
    getSystemCodes({ code_etype: 'equation parameters type', is_active: true }).then(setParaTypeCodes).catch(() => {});
  }, []);

  const openAdd = () => setModalEntry(null);              // null = 新增模式
  const openEdit = (entry: EquationEntry) => setModalEntry(entry);
  const closeModal = () => setModalEntry(undefined);

  const handleDataChange = (d: SectionB72Detail) => setData(d);

  const handleDelete = async (id: string) => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: t('sectionB72.deleteConfirm'),
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await sectionB72Service.deleteEquation(selectedProjectId, id);
      const res = await sectionB72Service.getSectionB72ByProject(selectedProjectId);
      setData(res);
    } catch { toast.error(t('sectionB72.deleteFailed')); }
  };

  const handleReset = async () => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '操作確認' : 'Confirmation',
      message: t('sectionB72.resetConfirm'),
      confirmText: isZh ? '重設' : 'Reset',
    });
    if (!ok) return;
    try {
      await sectionB72Service.resetSectionB72(selectedProjectId);
      setData(null); setModalEntry(undefined);
    } catch (err) { console.error(err); }
  };

  const equations = data?.equations ?? [];

  if (permissionLoading) return <div className="page-container"><div className="loading">Loading...</div></div>;
  if (!hasPermission('section_b7_2', 'read')) return <div className="page-container"><div className="error-message">{t('sectionB72.noPermission')}</div></div>;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b7_2" />
        {canEdit && data && selectedProjectId && (
          <div className="page-header-right">
            <button onClick={handleReset}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              title={t('sectionB72.resetTitle')}>
              🗑 {t('sectionB72.resetData')}
            </button>
          </div>
        )}
      </div>

      {/* 未選專案 */}
      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('sectionB72.selectProject')}
        </div>
      ) : loading ? (
        <div className="loading">{t('sectionB72.loading')}</div>
      ) : (
        <div>
          {/* 新增按鈕 */}
          {canEdit && (
            <div style={{ marginBottom: 16 }}>
              <button onClick={openAdd}
                style={{ padding: '8px 18px', fontSize: 13, borderRadius: 6, border: '1px solid #4A90E2', background: '#4A90E2', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                + {t('sectionB72.addEquation')}
              </button>
            </div>
          )}

          {/* DataTable */}
          {equations.length > 0 ? (
            <div className="table-container">
            <table className="data-table" style={{ fontSize: 13 }}>
              <thead className="table-header-dark-green">
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{t('sectionB72.colMethodology')}</th>
                  <th>{t('sectionB72.colEquation')}</th>
                  <th>{t('sectionB72.colParameters')}</th>
                  <th>{t('sectionB72.colResult')}</th>
                  <th style={{ width: 70, textAlign: 'center' }}>{t('sectionB72.colSample')}</th>
                  <th style={{ width: 160, textAlign: 'center' }}>{t('sectionB72.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {equations.map((entry, i) => (
                  <tr key={entry.id}>
                    <td style={{ textAlign: 'center' }}>{i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{entry.methodology || '—'}</div>
                    </td>
                    <td>
                      {entry.equation ? (
                        <div style={{ maxWidth: 280, overflow: 'auto' }}>
                          {(() => { try { return <BlockMath math={entry.equation} />; } catch { return <code>{entry.equation}</code>; } })()}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      {(entry.method_choices || []).length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                          {entry.method_choices.map((p, pi) => (
                            <li key={pi}>
                              <code>{p.symbol}</code>
                              {p.annual_value != null && (
                                <span> = {fmtNum(p.annual_value)}{' '}
                                  {p.unit && (
                                    (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()
                                  )}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : '—'}
                    </td>
                    <td>
                      {entry.calculation_result?.value != null ? (
                        <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                          <span>{fmtNum(entry.calculation_result.value)}</span>
                          {entry.calculation_result.unit && (
                            <span style={{ fontSize: 12 }}>
                              {(() => { try { return <InlineMath math={entry.calculation_result.unit} />; } catch { return entry.calculation_result.unit; } })()}
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                        background: entry.is_sampling ? '#e8f4ff' : '#f5f5f5',
                        color: entry.is_sampling ? '#4A90E2' : '#999',
                      }}>
                        {entry.is_sampling ? t('sectionB72.yes') : t('sectionB72.no')}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button onClick={() => openEdit(entry)}
                          style={{ padding: '6px 16px', background: '#fff', border: '1px solid #4A90E2', color: '#4A90E2', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                          {t('common.edit')}
                        </button>
                        {canEdit && (
                          <button onClick={() => handleDelete(entry.id)}
                            style={{ padding: '6px 16px', background: '#fff', border: '1px solid #e53935', color: '#e53935', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <div style={{ padding: '48px', textAlign: 'center', color: '#aaa', fontSize: 14 }}>
              {t('sectionB72.noEquations')}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && selectedProjectId && (
        <EquationModal
          projectId={selectedProjectId}
          initialEntry={modalEntry ?? null}
          isZh={isZh}
          canEdit={canEdit}
          paraTypeCodes={paraTypeCodes}
          onClose={closeModal}
          onDataChange={handleDataChange}
        />
      )}
    </div>
  );
};

export default SectionB72Page;
